// Persisted state — auth token + in-progress match. Stored under a single
// localStorage key so it survives reloads.

import type { AuthToken } from "./api";

export interface MatchState {
  matchId: string;
  rev: string | null;               // CouchDB _rev after the doc is created
  created: boolean;                 // true once the MatchPhone PUT succeeded

  // Half-by-half timing. Half time is implicit: it starts at firstHalfEnd
  // and ends at secondHalfKickOff (the tester skips through it instantly,
  // so those two timestamps are equal in practice).
  firstHalfKickOff: number | null;
  firstHalfEnd: number | null;
  secondHalfKickOff: number | null;

  // When End Match was clicked. Always refers to whichever half was active.
  endTime: number | null;
}

export interface AppState {
  auth: AuthToken | null;
  match: MatchState | null;
}

const KEY = "refsix-live-api-tester:v4";

const DEFAULT_STATE: AppState = {
  auth: null,
  match: null,
};

export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function saveState(state: AppState): void {
  localStorage.setItem(KEY, JSON.stringify(state));
}

// ---------- derivation helpers (pure, unit-tested) ----------

export type HalfPhase = "none" | 1 | 2 | "ended";

// Where in the match are we?
//  - "none"   : not kicked off yet (or no match)
//  - 1        : 1st half is the active playing half
//  - 2        : 2nd half is the active playing half
//  - "ended"  : End Match was clicked
export function currentPhase(m: MatchState | null): HalfPhase {
  if (!m || !m.firstHalfKickOff) return "none";
  if (m.endTime) return "ended";
  if (m.secondHalfKickOff) return 2;
  return 1;
}

// The kickoff timestamp of the currently-active playing half. Used by the
// timer and by the in-play event POSTs so `minuteOfPlay` is computed against
// the right half.
export function activeKickOff(m: MatchState | null): number | null {
  const phase = currentPhase(m);
  if (!m) return null;
  if (phase === 2) return m.secondHalfKickOff;
  if (phase === 1) return m.firstHalfKickOff;
  // Match is ended — use the kickoff of whichever half was last active.
  if (phase === "ended") return m.secondHalfKickOff ?? m.firstHalfKickOff;
  return null;
}
